
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/branding-context';
import { UserTable } from '@/components/admin/UserTable';
import { UserFormModal, PasswordResetModal } from '@/components/admin/UserFormModal';
import { InviteUserModal } from '@/components/admin/InviteUserModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { formatFullName } from '@/lib/format';
import { useUsers } from '@/hooks/useUsers';
import { User, CreateUser, UpdateUser } from '@/types';

export default function UsersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding } = useBranding();
  const responsesEnabled = branding.responsesEnabled !== false;

  // Admin-only page
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/unauthorized', { replace: true });
    }
  }, [user, navigate]);

  const {
    users,
    isLoading,
    error,
    createUser,
    inviteUser,
    updateUser,
    deactivateUser,
    activateUser,
    deleteUser,
    resetPassword,
    fetchUsers,
  } = useUsers();

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Selected user for operations
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Operation loading states
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Open create modal
  const handleOpenCreateModal = () => {
    setSelectedUser(null);
    setIsFormModalOpen(true);
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormModalOpen(true);
  };

  // Open password reset modal
  const handleOpenPasswordReset = (user: User) => {
    setSelectedUser(user);
    setIsPasswordModalOpen(true);
  };

  // Open deactivate confirmation
  const handleOpenDeactivate = (user: User) => {
    setSelectedUser(user);
    setIsDeactivateDialogOpen(true);
  };

  // Open delete confirmation (only for deactivated users)
  const handleOpenDelete = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  // Handle activate user
  const handleActivate = async (user: User) => {
    setIsSubmitting(true);
    await activateUser(user.id);
    setIsSubmitting(false);
  };

  // Handle form submission (create/update)
  const handleFormSubmit = async (data: CreateUser | UpdateUser) => {
    setIsSubmitting(true);
    
    if (selectedUser) {
      // Update existing user
      const result = await updateUser(selectedUser.id, data as UpdateUser);
      if (result) {
        setIsFormModalOpen(false);
      }
    } else {
      // Create new user
      const result = await createUser(data as CreateUser);
      if (result) {
        setIsFormModalOpen(false);
      }
    }
    
    setIsSubmitting(false);
  };

  // Handle password reset
  const handlePasswordReset = async (newPassword: string) => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    const success = await resetPassword(selectedUser.id, newPassword);
    if (success) {
      setIsPasswordModalOpen(false);
    }
    setIsSubmitting(false);
  };

  // Handle deactivate confirmation
  const handleDeactivateConfirm = async () => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    const success = await deactivateUser(selectedUser.id);
    if (success) {
      setIsDeactivateDialogOpen(false);
    }
    setIsSubmitting(false);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    const success = await deleteUser(selectedUser.id);
    if (success) {
      setIsDeleteDialogOpen(false);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Header */}
      <PageHeader
        title="User Management"
        description="Manage staff accounts and permissions"
        actions={
          <>
            <Button
              variant="outline"
              onClick={handleOpenInviteModal}
              leftIcon={<EnvelopeIcon className="w-4 h-4" />}
            >
              Invite User
            </Button>
            <Button onClick={handleOpenCreateModal} leftIcon={<PlusIcon className="w-4 h-4" />}>
              Add User
            </Button>
          </>
        }
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchUsers}
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Users Table */}
      <UserTable
        users={users}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDeactivate={handleOpenDeactivate}
        onActivate={handleActivate}
        onDelete={handleOpenDelete}
        onResetPassword={handleOpenPasswordReset}
        responsesEnabled={responsesEnabled}
      />

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSubmit={async (data) => {
          setIsSubmitting(true);
          const result = await inviteUser(data);
          setIsSubmitting(false);
          return result;
        }}
        isLoading={isSubmitting}
        responsesEnabled={responsesEnabled}
      />

      {/* Create/Edit Modal */}
      <UserFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleFormSubmit}
        user={selectedUser}
        isLoading={isSubmitting}
        responsesEnabled={responsesEnabled}
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSubmit={handlePasswordReset}
        user={selectedUser}
        isLoading={isSubmitting}
      />

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeactivateDialogOpen}
        onClose={() => setIsDeactivateDialogOpen(false)}
        onConfirm={handleDeactivateConfirm}
        title="Deactivate User"
        message={
          <>
            Are you sure you want to deactivate{' '}
            <strong>{formatFullName(selectedUser?.firstName, selectedUser?.lastName) || selectedUser?.email}</strong>?
            <br />
            <br />
            They will no longer be able to log in until reactivated.
          </>
        }
        confirmLabel="Deactivate"
        variant="danger"
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User Permanently"
        message={
          <>
            Are you sure you want to permanently delete{' '}
            <strong>{formatFullName(selectedUser?.firstName, selectedUser?.lastName) || selectedUser?.email}</strong>?
            <br />
            <br />
            This cannot be undone. All account data will be removed.
          </>
        }
        confirmLabel="Delete"
        variant="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4v16m8-8H4"
      />
    </svg>
  );
}

function EnvelopeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}
